import React, { useState, useEffect, useRef } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import MathDisplay from "./MathDisplay";
import Expression from "./Expression";
import Solve from "./Solve";
import { FaMicrophone, FaStopCircle } from "react-icons/fa";
import { TbCopy } from "react-icons/tb";
// import { addStyles, EditableMathField } from "react-mathquill";
import {
  algebraToLatex,
  toMathSymbols,
  getElement,
  equalizeParenthesis,
} from "./utils";
import DesktopHelp from "./DesktopHelp";
import MobileHelp from "./MobileHelp";

// import { TbMathFunction } from "react-icons/tb";
// import { RiAddFill, RiSubtractFill } from "react-icons/ri";

// // for mathquill:
// // inserts the required css to the <head> block.
// // you can skip this, if you want to do that by yourself.
// addStyles();

const NUMBER_WORDS = [
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
];
const trigFunctionReplace = () => {
  const long = ["cosine", "sine", "cotangent", "tangent", "cosecant", "secant"];
  const short = ["cos", "sin", "cot", "tan", "csc", "sec"];
  let result = [];
  long.forEach((l, i) => {
    result.push({
      text: l,
      symbol: short[i],
    });
  });
  return result;
};
const keywords = [
  {
    text: "overall",
    symbol: "/(",
  },
  {
    text: "squared",
    symbol: "^2",
  },
  {
    text: "cubed",
    symbol: "^3",
  },
  {
    text: "over",
    symbol: "/",
  },
  {
    text: "downstairs",
    symbol: "/",
  },
  {
    text: "equals",
    symbol: "=",
  },
  {
    text: "equal",
    symbol: "=",
  },
  {
    text: "all",
    symbol: "(",
  },
  {
    text: "everything",
    symbol: "((",
  },

  {
    text: "and",
    symbol: ")",
  },
  {
    text: "then",
    symbol: ")",
  },
  {
    text: ".",
    symbol: "",
  },
  {
    text: "plus",
    symbol: "+",
  },
  {
    text: "minus",
    symbol: "-",
  },
  {
    text: "divided",
    symbol: "/",
  },
  {
    text: "multiplied",
    symbol: "*",
  },
  {
    text: "times",
    symbol: "*",
  },
  // need to convert back to theta afterwards because latex converter doesn't recognize theta symbol
  // but need to symbol when putting parenthesis around symbol
  {
    text: "theta",
    symbol: "θ",
  },
  ...trigFunctionReplace(),
  {
    text: "by",
    symbol: "",
  },
  {
    text: "of",
    symbol: "",
  },
  {
    text: "is",
    symbol: "",
  },
  // {
  //   text: "to",
  //   symbol: "",
  // },
  {
    text: "the",
    symbol: "",
  },
  {
    text: "that",
    symbol: "",
  },
  {
    text: "power",
    symbol: "^",
  },
]; // operations that don't need parenthesis
const keyphrases = [
  {
    text: "integral",
    symbol: "int",
    callback: (equ) => {
      // doesnt think about multiple integrals
      const start = equ.indexOf("from");
      const end = equ.indexOf("to");
      let result = equ;
      if (start !== -1) {
        result = result.replace("from", "_");
        // result = equ.replaceAll('from', "").replaceAll('to', "").replaceAll(
        //   "integral",
        //   `int_{${getElement(equ, start + 1)}}^{${getElement(
        //     equ,
        //     end + 1
        //   )} }`
        // )
      }
      if (end !== -1) {
        result = result.replace("to", "^");
        const upperBound = getElement(result, result.indexOf("^") + 1);
        result = result.replace("^" + upperBound, "^(" + upperBound + ")");
      }
      result = result.replaceAll("integral", "int ");

      return result;
    },
  },
  {
    text: "derivative",
    symbol: "d/dx ",
    callback: (equ) => {
      // console.log(equ);
      if (equ.indexOf("θ") !== -1) {
        return equ.replace("derivative", "d/dθ ");
      }
      return equ.replace("derivative", "d/dx ");
    },
  },
];

const defaultEquation = "int (2-sin(2θ))/(cos(2θ)dθ)";

const defaultLatex =
  "\\int \\frac{2-\\sin\\left(2 \\theta\\right)}{\\cos\\left(2 \\theta\\right) dtheta}";
function App() {
  const [math, setMath] = useState();
  const [equation, setEquation] = useState(defaultEquation);
  const mathInput = useRef(null);
  const equationRef = useRef(null);
  const latexRef = useRef(null);
  const [latex, setLatex] = useState(defaultLatex);
  const [listening, setListening] = useState(false);

  const {
    transcript,
    // listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  useEffect(() => {
    if (math == null) return;
    if (math === "") {
      setEquation(defaultEquation);
      setLatex(defaultLatex);
      mathInput.current.value = "";
      return;
    }

    let words = math.toLowerCase().split(" ");

    if (words.length < 1) return;
    if (words.indexOf("solve") !== -1) {
      SpeechRecognition.stopListening();
      return;
    }

    words = words.map((e) => {
      let filtered = e.replaceAll(" ", "").replaceAll(",", "");
      const i = NUMBER_WORDS.indexOf(filtered);
      if (i !== -1) {
        return i + 1;
      }
      keywords.forEach((key) => {
        // console.log(filtered, key.text, filtered.indexOf(key.text));
        filtered = filtered.replaceAll(key.text, key.symbol);
      });
      return filtered;
    }); // remove spaces from words
    // console.log("after filter:", words);
    words = equalizeParenthesis(words).join("");
    console.log(algebraToLatex("int_x^pi sin(x)"));

    let equ = toMathSymbols(
      words,
      [
        "sin",
        "cos",
        "tan",
        "csc",
        "sec",
        "cot",
        "squareroot",
        "sqrt",
        "naturallog",
        "ln",
      ],
      [
        "sin",
        "cos",
        "tan",
        "(1/sin)",
        "(1/cos)",
        "(1/tan)",
        "sqrt",
        "sqrt",
        "ln",
        "ln",
      ]
    );

    keyphrases.forEach(({ text, symbol, callback = null }) => {
      if (callback && equ.indexOf(text) !== -1) {
        equ = callback(equ);
      } else {
        equ = equ.replaceAll(text, symbol);
      }
    });
    // console.log("equation", equ);

    // const postfix = toPostfix(equ);
    // console.log("postfix:", postfix);
    setEquation(equ);
    setLatex(algebraToLatex(equ.replaceAll("θ", "theta")));
  }, [math]);

  useEffect(() => {
    mathInput.current.value = transcript;
    setMath(transcript);
  }, [transcript]);

  useEffect(() => {
    if (listening) SpeechRecognition.startListening({ continuous: true });
    else SpeechRecognition.stopListening();
  });

  if (!browserSupportsSpeechRecognition) {
    return <span>Browser doesn't support speech recognition.</span>;
  }

  function copyRefTextToClipboard(ref) {
    // Get the text to copy from the div element
    const textToCopy = ref.current.innerText;

    // Create a temporary textarea element to hold the text
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;

    // Add the textarea to the document
    document.body.appendChild(textarea);

    // Select the contents of the textarea
    textarea.select();

    // Copy the contents to the clipboard
    document.execCommand("copy");

    // Remove the textarea from the document
    document.body.removeChild(textarea);
  }

  return (
    <main className="w-full  h-full min-h-full min-w-full flex items-center justify-center flex-col overflow-x-hidden section-padding py-10">
      <button className="btn-accent btn-primary btn hidden"></button>

      <section className=" max-w-[80%] w-[70%] flex flex-col gap-5">
        <div>
          <p className="font-light text-lg mb-2">Expression:</p>
          <div className="bg-base-300 shadow-lg  mt-5 rounded-xl  text-center text-xl shadow-xl	 px-10 py-3 overflow-x-auto ">
            <Expression expression={latex}></Expression>
          </div>
        </div>
        <div className="font-primary flex flex-col gap-5">
          <div>
            <p className="font-light text-lg mb-2">Input:</p>
            <div className="w-full p-0 relative">
              <textarea
                onChange={(e) => setMath(e.target.value)}
                ref={mathInput}
                contentEditable={true}
                className="block textarea w-full textarea-primary bg-neutral h-max resize-y py-1"
                placeholder="Integral of all 2 minus sine of all two Theta and then over all cosine of all two Theta and then D Theta."
              ></textarea>
              <button
                // onTouchStart={() => setListening((l) => !l)}
                onMouseDown={() => setListening((l) => !l)}
                // onTouchEnd={() => stopListening()}
                // onMouseUp={() => setListening()}
                className={`btn btn-circle absolute ring-4 ring-base-200 ring-offset-0 -top-4 -right-5 transform scale-[85%]  ${
                  listening ? "btn-accent" : "btn-primary"
                }`}
              >
                {listening ? <FaStopCircle /> : <FaMicrophone />}
              </button>
            </div>
            <div className="flex w-full py-1 m-0">
              {/* The button to open modal */}
              <div className="flex-1">
                <label
                  htmlFor="my-modal-4"
                  className="underline cursor-pointer text-primary"
                >
                  Help & Keywords
                </label>

                {/* Put this part before </body> tag */}
                <input
                  type="checkbox"
                  id="my-modal-4"
                  className="modal-toggle"
                />
                <label htmlFor="my-modal-4" className="modal cursor-pointer ">
                  <label
                    className="modal-box relative max-w-[80%] lg:max-w-[65%] w-max"
                    htmlFor=""
                  >
                    <h3 className="text-xl font-bold">Instructions</h3>
                    <p className="pt-4">
                      You can either speak or type in your epxression which will
                      be converted to an algebraic expression and LaTeX.
                    </p>
                    <p className="pt-4">
                      Then, hit the solve button to see your solution!
                    </p>
                    <h3 className="text-xl pt-4 font-bold">Keywords</h3>
                    <p className="pt-4">
                      When entering your math, you can use everyday language
                      instead of jargon like{" "}
                      <kbd className="kbd w-max">int</kbd> or{" "}
                      <kbd className="kbd w-max">sqrt</kbd>. Ofcourse those work
                      too. Here's a complete list of supported keywords:
                    </p>
                    <section className="hidden lg:block">
                      <DesktopHelp />
                    </section>
                    <section className="lg:hidden">
                      <MobileHelp />
                    </section>
                  </label>
                </label>
              </div>
              <button
                onClick={() => {
                  resetTranscript();
                  setMath("");
                }}
                className="hover:text-warning hover:transform hover:scale-110 mt-2 ml-auto float-right"
              >
                Clear
              </button>
            </div>
            {equation && (
              <>
                <p className="font-light text-lg mb-2 mt-6">Processed:</p>
                <div className="rounded-md h-max flex items-center justify-start w-full input-bordered input-md bg-neutral gap-2 p-2">
                  <div className="flex-1 px-2 break-all" ref={equationRef}>
                    {" "}
                    {equation}{" "}
                  </div>
                  <div
                    className="tooltip tooltip-primary"
                    data-tip="copy equation to clipboard"
                  >
                    <button
                      className="btn btn-primary rounded-md px-3"
                      onClick={() => copyRefTextToClipboard(equationRef)}
                    >
                      <TbCopy />
                    </button>
                  </div>
                </div>
              </>
            )}
            {latex && (
              <>
                <p className="font-light text-lg my-2">LaTeX:</p>
                <div className="rounded-md h-max flex items-center justify-start w-full input-bordered input-md bg-neutral gap-2 p-2">
                  <div className="flex-1 px-2 break-all" ref={latexRef}>
                    {" "}
                    {latex}{" "}
                  </div>
                  <div
                    className="tooltip tooltip-primary"
                    data-tip="copy LaTeX to clipboard"
                  >
                    <button
                      className="btn btn-primary rounded-md px-3"
                      onClick={() => copyRefTextToClipboard(latexRef)}
                    >
                      <TbCopy />
                    </button>
                  </div>
                </div>
              </>
            )}

            <Solve expression={latex} />
          </div>
        </div>
        <p className="mt-2 mb-0 py-0"> </p>
        <p className="mt-0 py-0">
          ©2023 Speak Math by{" "}
          <a
            className="underline text-primary "
            href="https://hammaadmemon.com"
            rel="noreferrer"
            target="_blank"
          >
            Hammaad Memon
          </a>
        </p>
      </section>
    </main>
  );
}

export default App;
